import "./App.css";
import { Routes, Route } from "react-router-dom";
import { Suspense, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setUser } from "./store/modules/user";
import { useNavigate, useLocation } from "react-router-dom";
import { closeSession, getUsers } from "./services/users";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";
import { ToastProvider } from "react-toast-notifications";
import { getPeople } from "./services/people.js";
import { isOfflineMode, checkNetworkStatus } from "./utils/networkUtils";
import { ChatProvider } from "./contexts/ChatContext";
import { SocketProvider } from "./contexts/SocketContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { PageTitleProvider } from "./contexts/PageTitleContext";
import Tags from "./pages/Tags/Tags.jsx";
import Main from "./pages/Main/Main";
import Ticket from "./pages/Ticket/Ticket.jsx";
import Articles from "./pages/Articles/Articles.jsx";
import Expenses from "./pages/Expenses/Expenses.jsx";
import Intro from "./pages/Intro/Intro.jsx";
import Customers from "./pages/Customer/Customer.jsx";
import Usage from "./pages/Usage/Usage.jsx";
import { createPlatformImport } from "./utils/importUtils";
import PrintTicketPage from "./pages/Orders/PrintTicketPage";

// Use platform-specific imports
const People = createPlatformImport(() => import("./pages/People/People"));
const Thanks = createPlatformImport(() => import("./pages/Thanks/Thanks.jsx"));
const Charts = createPlatformImport(() => import("./pages/Charts/Charts.jsx"));
const NotFound = createPlatformImport(() => import("./pages/NotFound/NotFound.jsx"));
const Auth = createPlatformImport(() => import("./pages/Auth/Auth"));
const PeopleSignIn = createPlatformImport(() => import("./pages/Auth/PeopleSignin.jsx"));
const SignIn = createPlatformImport(() => import("./pages/Auth/SignIn"));
const SignUp = createPlatformImport(() => import("./pages/Auth/SignUp"));
const FreeTrialThanks = createPlatformImport(() => import("./pages/Auth/FreeTrialThanks"));
const SubscriptionThanks = createPlatformImport(() => import("./pages/Payment/SubscriptionThanks"));
const Home = createPlatformImport(() => import("./pages/Home/Home"));
const Dashboard = createPlatformImport(() => import("./pages/Dashboard/Dashboard"));
const ChartsV2 = createPlatformImport(() => import("./pages/DashboardV2/DashboardV2"));
const WebEditor = createPlatformImport(() => import("./pages/WebEditor/WebEditor"));
const Products = createPlatformImport(() => import("./pages/Products/Products"));
const Users = createPlatformImport(() => import("./pages/Users/Users"));
const Sell = createPlatformImport(() => import("./pages/Sell/Sell"));
const Payment = createPlatformImport(() => import("./pages/Payment/Payment"));
const Orders = createPlatformImport(() => import("./pages/Orders/Orders"));
const SignInEmail = createPlatformImport(() => import("./pages/Auth/SignInEmail"));
const ForgotPassword = createPlatformImport(() => import("./pages/Auth/ForgotPassword"));
const ResetPassword = createPlatformImport(() => import("./pages/Auth/ResetPassword"));
const SellVariable = createPlatformImport(() => import("./pages/SellVariable/SellVariable"));
const Conversations = createPlatformImport(() => import("./pages/Conversations/Conversations"));
const AIPrompt = createPlatformImport(() => import("./pages/AIPrompt/AIPrompt"));
const PendingOrders = createPlatformImport(() => import("./pages/PendingOrders/PendingOrders"));
const Permissions = createPlatformImport(() => import("./pages/Permissions/Permissions"));

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [uData, set_uData] = useState({});

  const verifyMember = async (user) => {
    if (isOfflineMode()) {
      set_uData(user);
      dispatch(setUser(user));
      return;
    }

    try {
      const memberData = await getPeople();

      const memberFromBackend = memberData[0];
      if (memberFromBackend?._id) {
        const updatedMemberData = {
          ...user,
          ...memberFromBackend,
          member: true,
          memberId: memberFromBackend._id,
          _id: memberFromBackend.userId,
        };
        localStorage.setItem("user", JSON.stringify(updatedMemberData));
        set_uData(updatedMemberData);
        dispatch(setUser(updatedMemberData));
      } else {
        closeSession();
      }
    } catch (error) {
      console.error("[Member verification] Error:", error);
      // If verification fails, check if we can use cached data
      if (user._id) {
        set_uData(user);
        dispatch(setUser(user));
      } else {
        closeSession();
      }
    }
  };

  const verifyMainUser = async (user) => {

    if (isOfflineMode()) {
      set_uData(user);
      dispatch(setUser(user));
      return;
    }

    try {
      const userQuery = await getUsers({ _id: user._id });
      const userFromBackend = userQuery[0];
      if (userFromBackend?._id) {
        const updatedUserData = {
          ...user,
          ...userFromBackend,
        };
        set_uData(updatedUserData);
        dispatch(setUser(updatedUserData));
        localStorage.setItem("user", JSON.stringify(updatedUserData));
      } else {
        closeSession();
      }
    } catch (error) {
      console.error("[User verification] Error:", error);
      // If verification fails, check if we can use cached data
      if (user._id) {
        set_uData(user);
        dispatch(setUser(user));
      } else {
        closeSession();
      }
    }
  };

  useEffect(() => {
    const verifyUser = async () => {
      const userData = localStorage.getItem("user");

      if (userData) {
        const user = JSON.parse(userData);

        // Check if offline mode - actually test connectivity
        const isCurrentlyOffline = await checkNetworkStatus();
        if (isCurrentlyOffline) {
          // Skip backend verification in offline mode, just use localStorage data
          set_uData(user);
          dispatch(setUser(user));

          // Navigate if needed
          if (
            pathname === "/" ||
            pathname === "/auth/signin" ||
            pathname === "/auth/signup"
          ) {
            navigate("/home");
          }
          return;
        }

        // Only perform online verification if we're online
        if (user.member) {
          await verifyMember(user);
        } else {
          await verifyMainUser(user);
        }

        // Navigate if needed
        setTimeout(() => {
          if (
            pathname === "/" ||
            pathname === "/auth/signin" ||
            pathname === "/auth/signup"
          ) {
            navigate("/home");
          }
        }, 100);
      }
    };
    verifyUser();
  }, []);

  return (
    <div className="App">
      <ToastProvider>
        <PageTitleProvider>
          <PermissionsProvider>
            <SocketProvider>
              <ChatProvider>
                <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route
                path="/"
                element={<Main />}
              />
              <Route
                path="/auth/signin/"
                element={
                  <Auth>
                    <SignIn />
                  </Auth>
                }
              />
              <Route
                path="/intro"
                element={<Intro />}
              />
              <Route
                path="/auth/signin-email/:token?"
                element={
                  <Auth>
                    <SignInEmail />
                  </Auth>
                }
              />
              <Route
                path="/auth/forgot-password"
                element={
                  <Auth>
                    <ForgotPassword />
                  </Auth>
                }
              />
              <Route
                path="/auth/reset-password/:token?"
                element={
                  <Auth>
                    <ResetPassword />
                  </Auth>
                }
              />
              <Route
                path="/auth/signin/code"
                element={
                  <Auth>
                    <SignIn />
                  </Auth>
                }
              />
              <Route
                path="/auth/signin"
                element={
                  <Auth>
                    <SignIn />
                  </Auth>
                }
              />
              <Route
                path="/access-user/:peopleId?/:name?"
                element={
                  <Auth>
                    <PeopleSignIn />
                  </Auth>
                }
              />
              <Route
                path="/auth/signup"
                element={
                  <Auth>
                    <SignUp />
                  </Auth>
                }
              ></Route>
              <Route
                path="/registro-completo"
                element={<FreeTrialThanks />}
              ></Route>
              <Route
                path="/compra-completada/:months"
                element={<SubscriptionThanks />}
              ></Route>
              <Route
                path="/home"
                element={
                  <Home>
                    <Dashboard />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/statistics"
                element={
                  <Home>
                    <ChartsV2 />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/pending-orders"
                element={
                  <Home>
                    <PendingOrders />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/products/:productId?/:productAction?"
                element={
                  <Home>
                    <Products />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/orders"
                element={
                  <Home>
                    <Orders />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/reporte-de-uso"
                element={
                  <Home>
                    <Usage />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/expenses"
                element={
                  <Home>
                    <Expenses />
                  </Home>
                }
              ></Route>
              <Route
                path="/home/sell"
                element={
                  <Home>
                    <Sell />
                  </Home>
                }
              />
              <Route
                path="/home/sell-variable"
                element={
                  <Home>
                    <SellVariable />
                  </Home>
                }
              />
              <Route
                path="/home/payment/:plan?"
                element={
                  <Home>
                    <Payment />
                  </Home>
                }
              />
              <Route
                path="/home/thanks"
                element={
                  <Home>
                    <Thanks />
                  </Home>
                }
              />
              <Route
                path="/home/charts"
                element={
                  <Home>
                    <Charts />
                  </Home>
                }
              />
              <Route
                path="/home/people"
                element={
                  <Home>
                    <People />
                  </Home>
                }
              />
              <Route
                path="/home/tags"
                element={
                  <Home>
                    <Tags />
                  </Home>
                }
              />
              <Route
                path="/home/ticket"
                element={
                  <Home>
                    <Ticket />
                  </Home>
                }
              />
              <Route
                path="/home/articles"
                element={
                  <Home>
                    <Articles />
                  </Home>
                }
              />
              <Route
                path="/home/customers"
                element={
                  <Home>
                    <Customers />
                  </Home>
                }
              />

              <Route
                path="/home/users"
                element={
                  <Home>
                    {uData.admin && uData.active ? <Users /> : null}
                  </Home>
                }
              ></Route>
              <Route
                path="/home/web-editor/:admin?"
                element={<WebEditor />}
              />
              <Route
                path="/crea-tu-pagina-web"
                element={<WebEditor />}
              />
              <Route
                path="/home/conversations"
                element={
                  <Home>
                    {uData.admin && uData.active ? <Conversations /> : null}
                  </Home>
                }
              />
              <Route
                path="/home/ai-prompt"
                element={
                  <Home>
                    {uData.admin && uData.active ? <AIPrompt /> : null}
                  </Home>
                }
              />
              <Route
                path="/home/permisos"
                element={
                  <Home>
                    <Permissions />
                  </Home>
                }
              />
              <Route
                path="/home/print-ticket/:orderId"
                element={<PrintTicketPage />}
              />
              <Route
                path="*"
                element={<NotFound />}
              />
            </Routes>
                </Suspense>
              </ChatProvider>
            </SocketProvider>
          </PermissionsProvider>
        </PageTitleProvider>
      </ToastProvider>
    </div>
  );
}

export default App;
